import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, doc, getDocs, addDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { ChevronUp, ChevronDown, Edit2, Trash2, Plus, X, Play, Save } from "lucide-react";

const EpisodeManager = ({ seriesId, seriesTitle, seriesThumbnail }) => {
  const [episodes, setEpisodes] = useState([]);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newEpisode, setNewEpisode] = useState({
    id: uuidv4(),
    title: "",
    videoUrl: "",
    language: "en-US",
    episodeNumber: 1,
    shortDescription: "",
    longDescription: "",
    releaseDate: new Date().toISOString(),
    rating: { rating: "0", ratingSource: "BBFC" },
    thumbnail: seriesThumbnail,
    duration: "00:00:01",
  });

  useEffect(() => {
    fetchEpisodes();
  }, [seriesId]);

  const fetchEpisodes = async () => {
    try {
      const episodesSnapshot = await getDocs(
        collection(db, `content/main/series/${seriesId}/episodes`)
      );
      const episodesData = episodesSnapshot.docs.map((doc) => ({
        episodeId: doc.id,
        ...doc.data(),
      }));
      setEpisodes(episodesData.sort((a, b) => a.episodeNumber - b.episodeNumber));
    } catch (error) {
      console.error("Error fetching episodes:", error);
    }
  };

  // Handle input changes and force ID to lowercase
  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
      setNewEpisode({ ...newEpisode, [name]: value });
  };


  const convertToFullUrl = (shortUrl) => {
    if (!shortUrl) return "";
    return shortUrl.endsWith(".mp4")
      ? `https://video.isilive.ca/${shortUrl}`
      : `https://linx.isilive.ca/live/${shortUrl}/index.m3u8`;
  };

  const handleAddEpisode = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
  
    setIsSubmitting(true);
    const fullVideoUrl = convertToFullUrl(newEpisode.videoUrl);
    const episode = {
      ...newEpisode,
      content: {
        dateAdded: new Date().toISOString(),
        duration: timeToSeconds(newEpisode.duration),
        language: "en-US",
        videos: [
          {
            url: fullVideoUrl,
            quality: "HD",
            videoType: newEpisode.videoUrl.endsWith(".mp4") ? "MP4" : "HLS",
          },
        ],
      },
    };
  
    try {
      await addDoc(
        collection(db, `content/main/series/${seriesId}/episodes`),
        episode
      );
      await fetchEpisodes();
      resetForm();
      setIsDropdownOpen(false);
    } catch (error) {
      console.error("Error adding episode:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditEpisode = async (e) => {
    e.preventDefault();
    if (!editingEpisode || isSubmitting) return;

    setIsSubmitting(true);
    const fullVideoUrl = convertToFullUrl(newEpisode.videoUrl);
    const updatedEpisode = {
      ...newEpisode,
      content: {
        ...newEpisode.content,
        duration: timeToSeconds(newEpisode.duration),
        videos: [
          {
            url: fullVideoUrl,
            quality: "HD",
            videoType: newEpisode.videoUrl.endsWith(".mp4") ? "MP4" : "HLS",
          },
        ],
      },
    };

    try {
      await updateDoc(
        doc(db, `content/main/series/${seriesId}/episodes/${editingEpisode.episodeId}`),
        updatedEpisode
      );
      await fetchEpisodes();
      resetForm();
      setEditingEpisode(null);
    } catch (error) {
      console.error("Error updating episode:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEpisode = async (episodeId) => {
    if (window.confirm("Are you sure you want to delete this episode?")) {
      try {
        await deleteDoc(
          doc(db, `content/main/series/${seriesId}/episodes/${episodeId}`)
        );
        await fetchEpisodes();
      } catch (error) {
        console.error("Error deleting episode:", error);
      }
    }
  };

  const handleMoveEpisode = async (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= episodes.length) return;

    const updatedEpisodes = [...episodes];
    const [movedEpisode] = updatedEpisodes.splice(fromIndex, 1);
    updatedEpisodes.splice(toIndex, 0, movedEpisode);

    // Update episode numbers
    const updates = updatedEpisodes.map((episode, index) => ({
      ...episode,
      episodeNumber: index + 1,
    }));

    try {
      await Promise.all(
        updates.map((episode) =>
          updateDoc(
            doc(db, `content/main/series/${seriesId}/episodes/${episode.episodeId}`),
            { episodeNumber: episode.episodeNumber }
          )
        )
      );
      setEpisodes(updates);
    } catch (error) {
      console.error("Error reordering episodes:", error);
    }
  };

  const timeToSeconds = (duration) => {
    if (!duration) return 0;
    if (typeof duration === "number") return duration;
    const [hours, minutes, seconds] = duration.split(":").map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  const secondsToTimeFormat = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const resetForm = () => {
    setNewEpisode({
      id: uuidv4(),
      title: "",
      videoUrl: "",
      language: "en-US",
      episodeNumber: episodes.length + 1,
      shortDescription: "",
      longDescription: "",
      releaseDate: new Date().toISOString(),
      rating: { rating: "0", ratingSource: "BBFC" },
      thumbnail: seriesThumbnail,
      duration: "00:00:01",
    });
    setEditingEpisode(null); // Reset editing series
  };

  return (
    <div className="max-w-6xl mx-auto p-4">

    <div className="w-full bg-gray-900 text-white rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-gray-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h2 className="text-xl md:text-2xl font-bold">Episodes - {seriesTitle}</h2>
        <button
  onClick={() => {
    if (!isDropdownOpen) {
      resetForm(); // Call resetForm only when opening the dropdown
    }
    setIsDropdownOpen(!isDropdownOpen);
  }}
  className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
>
  {isDropdownOpen ? <X size={20} /> : <Plus size={20} />}
  {isDropdownOpen ? "Cancel" : "Add New Episode"}
</button>
        </div>
      </div>
      {/* Add/Edit Series Form */}
      {isDropdownOpen && (
      <div className="p-4 md:p-6">
      <h3 className="text-xl font-bold mb-4">
        {editingEpisode ? "Edit Episode" : "Add New Episode"}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          name="title"
          value={newEpisode.title}
          onChange={handleInputChange}
          placeholder="Episode Title"
          className="w-full p-3 bg-gray-700 rounded-lg"
        />
        <input
          type="text"
          name="videoUrl"
          value={newEpisode.videoUrl}
          onChange={handleInputChange}          
          placeholder="Video URL (short format)"
          className="w-full p-3 bg-gray-700 rounded-lg"
        />
        <input
          type="number"
          name="episodeNumber"
          value={newEpisode.episodeNumber}
          onChange={handleInputChange}
          placeholder="Episode Number"
          className="w-full p-3 bg-gray-700 rounded-lg"
        />
        <input
          type="text"
          name="duration"
          value={newEpisode.duration}
          onChange={handleInputChange}
          placeholder="Duration (HH:MM:SS)"
          className="w-full p-3 bg-gray-700 rounded-lg"
        />
        <div className="col-span-1 md:col-span-2">
          <textarea
            name="shortDescription"
            value={newEpisode.shortDescription}
            onChange={handleInputChange}
            placeholder="Short Description"
            className="w-full p-3 bg-gray-700 rounded-lg"
            rows="2"
          />
        </div>
        <div className="col-span-1 md:col-span-2">
          <textarea
            name="longDescription"
            value={newEpisode.longDescription}
            onChange={handleInputChange}
            placeholder="Long Description"
            className="w-full p-3 bg-gray-700 rounded-lg"
            rows="3"
          />
        </div>
        <div className="col-span-1 md:col-span-2 flex gap-2">
        <button
        onClick={editingEpisode ? handleEditEpisode : handleAddEpisode}
        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg transition-colors"
      >
        {editingEpisode ? "Save Changes" : "Add Episode"}
      </button>
        </div>
      </div>
      </div>
)}
        <div className="space-y-4 m-2">
          {episodes.map((episode, index) => (
            <div
              key={episode.episodeId}
              className="bg-gray-800 rounded-lg p-4 shadow-lg transition-all hover:shadow-xl"
            >
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg md:text-xl font-semibold">
                      Episode {episode.episodeNumber}:
                    </span>
                    <h3 className="text-lg md:text-xl">{episode.title}</h3>
                  </div>
                  <p className="text-gray-400 mt-2">Short Description: {episode.shortDescription}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-2">
                    <span>Duration: {secondsToTimeFormat(episode.content?.duration)}</span>
                    <span>Video Type: {episode.content?.videos[0]?.videoType}</span>
                  </div>
                </div>
                <div className="flex gap-2 w-full md:w-auto justify-end">
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleMoveEpisode(index, index - 1)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={index === 0}
                    >
                      <ChevronUp size={20} />
                    </button>
                    <button
                      onClick={() => handleMoveEpisode(index, index + 1)}
                      className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={index === episodes.length - 1}
                    >
                      <ChevronDown size={20} />
                    </button>
                  </div>
                  <button
                    onClick={() => window.open("https://video.isilive.ca/play/" + (episode.videoUrl))}
                    className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                  >
                    <Play size={20} />
                  </button>
                  <button
                      onClick={() => {
                        setEditingEpisode(episode);
                        setNewEpisode(episode);
                        setIsDropdownOpen(true);
                      }}                    
                    className="p-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                  >
                    <Edit2 size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteEpisode(episode.episodeId)}
                    className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
    </div>
    </div>
  );
};

export default EpisodeManager;